// @flow
import * as React from 'react';
import {Component} from 'react';

import {
    ExperimentContext,
    type ExperimentProviderContextValue
} from './ExperimentProvider';

import type {AbacusEvaluationData} from '../../reducers/abacus';
import type {ExperimentValue} from './AbacusContext';

export const ON = 'on';
export const OFF = 'off';

type ExperimentVariant = 'on' | 'off' | ExperimentValue;

/**
 * Args exposed via renderProp function when using Experiment components with
 * function children:
 *  <Experiment name="MY_TEST" variant={1}>
 *      {({bucketConfig}) => <p>{bucketConfig}</p>}
 *  </Experiment>
 */
type ExperimentVariantRenderProps = {
    bucketConfig: $PropertyType<
        $PropertyType<AbacusEvaluationData, 'bucket'>,
        'bucketConfig'
    >,
    logExperiment: Function
};

type ExperimentVariantChildren =
    | React.Node
    | ((ExperimentVariantRenderProps) => React.Node);

type YieldedExperimentComponentProps = {children: ExperimentVariantChildren};
type YieldedVariantComponentProps = {
    children: ExperimentVariantChildren,
    variant: ExperimentVariant
};

type YieldedExperimentComponent = React.ComponentType<
    YieldedExperimentComponentProps
>;
type YieldedVariantComponent = React.ComponentType<
    YieldedVariantComponentProps
>;

type ExperimentRenderProps = {
    On: YieldedExperimentComponent,
    Off: YieldedExperimentComponent,
    Variant: YieldedVariantComponent
} & ExperimentVariantRenderProps;

type ExperimentChildren = React.Node | ((ExperimentRenderProps) => React.Node);

type WithExperimentProps = {
    context: ExperimentProviderContextValue,
    children: ExperimentChildren,
    deferLogging: boolean,
    name: string,
    variant?: ExperimentVariant
};

class WithExperiment extends Component<WithExperimentProps> {
    componentDidMount() {
        if (!this.props.deferLogging && this.evaluationData) {
            this.props.context.logExperimentOnce(this.evaluationData);
        }
    }

    get evaluationData() {
        const {context, name} = this.props;
        return context.abacus[name];
    }

    /**
     * Property initializers for functions passed down via renderProp children interface:
     *
     * - deferredExperimentLogger
     * - On component
     * - Off component
     * - Variant component
     */
    deferredExperimentLogger = () => {
        if (this.props.deferLogging) {
            return this.props.context.logExperimentOnce(this.evaluationData);
        }

        // eslint-disable-next-line no-console
        return console.warn(
            `Warning: called yielded logExperiment function for experiment ${
                this.props.name
            }, but experiment was already logged during componentDidMount. To defer logging, add the "deferLogging" prop to your <Experiment> component.`
        );
    };

    On = ({children}) => {
        this.throwIfHasVariantProp();
        return this.experimentIsVariant(ON) ? children : null;
    };

    Off = ({children}) => {
        this.throwIfHasVariantProp();
        return this.experimentIsVariant(OFF) ? children : null;
    };

    Variant = ({children, variant}) => {
        this.throwIfHasVariantProp();
        return this.experimentIsVariant(variant) ? children : null;
    };

    /**
     * Instance methods
     */
    throwIfHasVariantProp() {
        if (
            this.props.variant !== null &&
            typeof this.props.variant !== 'undefined'
        ) {
            const variant = Array.isArray(this.props.variant)
                ? `[${this.props.variant.join(',')}]`
                : this.props.variant;

            throw new Error(
                `Used On, Off, or Variant components with <Experiment name="${
                    this.props.name
                }" variant={${variant}}/>. The variant prop overrides yielded components and not all branches may be reachable. For best results, remove the variant prop if you want to use the yielded On, Off, or Variant components.`
            );
        }
    }

    experimentIsVariant(variant) {
        const {context} = this.props;
        const {evaluationData} = this;

        if (variant === 'on') {
            return context.evaluationIsOn(evaluationData);
        }

        if (variant === 'off') {
            return context.evaluationIsVariant(evaluationData, 0);
        }

        return this.props.context.evaluationIsVariant(evaluationData, variant);
    }

    hasVariantProp() {
        return (
            this.props.variant !== null &&
            typeof this.props.variant !== 'undefined'
        );
    }

    requireChildrenFunctionIfNoVariantProp() {
        throw new Error(
            `A children function is required when using <Experiment name="${
                this.props.name
            }"> without a "variant" prop. Either specify a variant, or pass a child function and use the supplied On, Off, or Variant components.
Example:
<Experiment name="MY_TEST">
    {({On}) => (
        <p>The test is on!</p>
    )}
</Experiment>`
        );
    }

    render() {
        const {children, variant} = this.props;
        const evaluationData = this.evaluationData || {bucket: {}};
        const {
            bucket: {bucketConfig}
        } = evaluationData;
        const {
            On,
            Off,
            Variant,
            deferredExperimentLogger: logExperiment
        } = this;

        if (!this.hasVariantProp() && typeof children !== 'function') {
            this.requireChildrenFunctionIfNoVariantProp();
        }

        if (typeof children === 'function') {
            return children({
                bucketConfig,
                logExperiment,
                On,
                Off,
                Variant
            });
        }

        if (!this.experimentIsVariant(variant)) {
            return null;
        }

        return children;
    }
}

type ExperimentProps = {
    children: ExperimentChildren,
    deferLogging: boolean,
    name: string,
    variant?: ExperimentVariant
};

/**
 * Default Experiment component. In its most basic form, renders the child content
 * if bucketed into the passed experiment name in the given variant:
 *
 * ```jsx
 *  <Experiment name="MY_EXPERIMENT" variant={1}>
 *      Children render if MY_EXPERIMENT is active and we're bucketed into variant 1.
 *      An exposure is logged.
 *  </Experiment>
 * ```
 *
 * Alternately, pass 'on' or 'off' if it doesn't matter what variant is active:
 *
 * ```jsx
 * <Experiment name="MY_EXPERIMENT" variant={'on'}>
 * <Experiment name="MY_EXPERIMENT" variant={Experiment.ON}>
 *
 * <Experiment name="MY_EXPERIMENT" variant={'off'}>
 * <Experiment name="MY_EXPERIMENT" variant={Experiment.OFF}>
 * <Experiment name="MY_EXPERIMENT" variant={0}> // renders in both control and inactive states
 * ```
 *
 * Pass an array to render if any of the supplied variants is active:
 *
 * ```jsx
 * <Experiment name="MY_EXPERIMENT" variant={[1, 3]}>
 * ```
 *
 * Pass a child function to recieve:
 * - `bucketConfig` for the test (string data configured in the TNL dashboard)
 * - `logExperiment` for manually logging experiment in response to user action
 *      - requires `deferLogging` prop on parent component
 *      - logs a warning if `deferLogging` is missing
 *
 * ```jsx
 * <Experiment name="MY_EXPERIMENT" deferLogging>
 *      {({On, Off, bucketConfig: insultingAdjective, logExperiment}) => (
 *          <On>
 *              <MessageShower onClick={logExperiment} label={'Click me'}>
 *                  <p>You have an {insultingAdjective} face</p>
 *              </MessageShower>
 *          </On>
 *          <Off>
 *              <MessageShower onClick={logExperiment} label={'Click me'}>
 *                  <p>Gosh thanks for clicking</p>
 *              </MessageShower>
 *          </Off>
 *      )}
 * </Experiment>
 * ```
 *
 * A warning will be logged if you call the yielded `logExperiment` but forget to
 * set `deferLogging` on the Experiment component:
 *
 * ```jsx
 * <Experiment name="MY_EXPERIMENT" variant={Experiment.ON}>
 *      {({logExperiment}) => (
 *          <button onClick={logExperiment}>Click me</button> // <-- logs a console warning when the button is clicked
 *      )}
 * </Experiment>
 * ```
 *
 * When passed a child function, Experiment will also yield On, Off, and Variant
 * components, bound to the specified test name, to help express your test more concisely.
 * Wrap in a <React.Fragment> to render multiple components side-by-side:
 *
 * ```jsx
 *  <Experiment name="MY_TEST">
 *      {({On, Off}) => (
 *          <React.Fragment>
 *              <On>
 *                  <p>Hey check out this new component!</p>
 *              </On>
 *              <Off>
 *                  <p>Old, boring, musty component</p>
 *              </Off>
 *          </React.Fragment>
 *      )}
 *  </Experiment>
 * ```
 *
 * Usage w/ variants, including multi-variant matching:
 *  <Experiment name="MY_TEST">
 *      {({Variant, Off}) => (
 *          <React.Fragment>
 *              <Variant variant={1}>
 *                  <p>Hey check out this new component!</p>
 *              </Variant>
 *              <Variant variant={[1,2]}>
 *                  <p>I'll render in both variant 1 and variant 2!
 *              </Variant>
 *              <Off>
 *                  <p>Old, boring, musty component</p>
 *              </Off>
 *          </React.Fragment>
 *      )}
 *  </Experiment>
 *
 * Usage w/ renderProps to access bucketConfig:
 *  <Experiment name="MY_TEST">
 *      {({On, Off, bucketConfig}) => (
 *          <React.Fragment>
 *              <On>
 *                  <p>Hey check out this new {bucketConfig} component!</p>
 *              </On>
 *              <Off>
 *                  <p>Old, boring, musty component</p>
 *              </Off>
 *          </React.Fragment>
 *      )}
 *  </Experiment>
 *
 * An error will be thrown if you try to use one of the yielded components alongside
 * a top-level `variant` prop on the Experiment component itself.
 */
const Experiment = ({
    children,
    deferLogging,
    name,
    variant
}: ExperimentProps) => {
    return (
        <ExperimentContext.Consumer>
            {(abacusContext) => (
                <WithExperiment
                    context={abacusContext}
                    deferLogging={deferLogging}
                    name={name}
                    variant={variant}
                >
                    {children}
                </WithExperiment>
            )}
        </ExperimentContext.Consumer>
    );
};
Experiment.defaultProps = {
    deferLogging: false
};
Experiment.displayName = 'Experiment';
Experiment.ON = ON;
Experiment.OFF = OFF;

export default Experiment;
